import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2, Save } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import axiosApi from "../server/service";

// --- TYPE DEFINITIONS ---
// Yeh Ids ko handle karne ke liye update kiya gaya hai
interface VendorFormData {
  id?: string; // Optional: Sirf edit mode mein use hoga
  vendorId: string;
  vendorName: string;
  vendorType: string;
  contactNumber: string;
  status: "Active" | "Inactive";
  emailAddress: string;
  gstNumber: string;
  address: string;
  additionalNotes: string;
}

interface FormErrors {
  [key: string]: string;
}

// --- MAIN COMPONENT ---
const CreateVendor: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation(); // React Router se data lene ke liye

  // --- Check if we are in "Edit" mode ---
  const vendorToEdit = location.state?.vendor as VendorFormData | null;
  const isEditing = !!vendorToEdit;

  // --- STATE MANAGEMENT ---
  const [formData, setFormData] = useState<Omit<VendorFormData, "id">>({
    vendorId: "",
    vendorName: "",
    vendorType: "",
    contactNumber: "",
    status: "Active",
    emailAddress: "",
    gstNumber: "",
    address: "",
    additionalNotes: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- EFFECT TO PRE-FILL FORM FOR EDITING ---
  // Jab component load hoga, yeh check karega ki kya edit ke liye data aaya hai
  useEffect(() => {
    if (isEditing && vendorToEdit) {
      // Form state ko purane data se update karein
      setFormData({
        vendorId: vendorToEdit.vendorId || "",
        vendorName: vendorToEdit.vendorName || "",
        vendorType: vendorToEdit.vendorType || "",
        contactNumber: vendorToEdit.contactNumber || "",
        status: vendorToEdit.status || "Active",
        emailAddress: vendorToEdit.emailAddress || "",
        gstNumber: vendorToEdit.gstNumber || "",
        address: vendorToEdit.address || "",
        additionalNotes: vendorToEdit.additionalNotes || "",
      });
    }
  }, [isEditing, vendorToEdit]);

  // --- FORM HANDLING (No changes here) ---
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // --- VALIDATION LOGIC (No changes here) ---
  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {};
    if (!formData.vendorName.trim())
      newErrors.vendorName = "Vendor name is required.";
    if (!formData.vendorType)
      newErrors.vendorType = "Please select a vendor type.";
    if (!formData.contactNumber.trim())
      newErrors.contactNumber = "Contact number is required.";
    else if (
      !/^\+?[1-9]\d{1,14}$/.test(formData.contactNumber.replace(/\s/g, ""))
    ) {
      newErrors.contactNumber = "Please enter a valid phone number.";
    }
    if (formData.emailAddress && !/\S+@\S+\.\S+/.test(formData.emailAddress)) {
      newErrors.emailAddress = "Please enter a valid email address.";
    }
    return newErrors;
  };

  // --- UPDATED FORM SUBMISSION LOGIC ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fix the errors before saving.");
      return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading(
      isEditing ? "Updating vendor..." : "Saving vendor..."
    );

    try {
      let response;
      if (isEditing) {
        // Update request bhejhein
        response = await axiosApi.put(
          `/api/vendors/update-vendor/${vendorToEdit?.id}`,
          formData
        );
      } else {
        // Create request bhejhein
        response = await axiosApi.post("/api/vendors/create-vendor", formData);
      }

      toast.success(response.data.message, { id: toastId });

      setTimeout(() => {
        navigate("/vendormaster");
      }, 1000);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage, { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- JSX RENDER (Original UI with dynamic text) ---
  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className=" bg-gray-50 min-h-screen ">
        <form onSubmit={handleSubmit} noValidate>
          {/* Header Section */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-black">
                {isEditing ? "Edit Vendor" : "Create New Vendor"}
              </h1>
              <p className="text-gray-500 mt-1">
                Configure vendor information and contact details
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/vendormaster"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg shadow-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {isEditing ? "Update Vendor" : "Save Vendor"}
              </button>
            </div>
          </div>

          {/* Form Body (Original CSS and structure is maintained) */}
          <div className="bg-white p-6 md:p-8 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800 mb-6">
              Vendor Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              {/* Vendor ID */}
              <div>
                <label
                  htmlFor="vendorId"
                  className="block text-sm font-medium text-gray-700"
                >
                  Vendor ID
                </label>
                <input
                  type="text"
                  id="vendorId"
                  name="vendorId"
                  value={formData.vendorId}
                  onChange={handleChange}
                  placeholder="e.g., V001"
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  // required 
                />
              </div>

              {/* Vendor Name */}
              <div>
                <label
                  htmlFor="vendorName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Vendor Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="vendorName"
                  name="vendorName"
                  value={formData.vendorName}
                  onChange={handleChange}
                  placeholder="Enter vendor name"
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.vendorName ? "border-red-500" : "border-gray-300"
                  } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.vendorName && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.vendorName}
                  </p>
                )}
              </div>

              {/* Vendor Type */}
              <div>
                <label
                  htmlFor="vendorType"
                  className="block text-sm font-medium text-gray-700"
                >
                  Vendor Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="vendorType"
                  name="vendorType"
                  value={formData.vendorType}
                  onChange={handleChange}
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.vendorType ? "border-red-500" : "border-gray-300"
                  } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                >
                  <option value="" disabled>
                    Select type
                  </option>
                  <option value="Fabric Supplier">Fabric Supplier</option>
                  <option value="Embroidery">Embroidery</option>
                  <option value="DesignWork">Design Work</option>
                  <option value="Handwork">Handwork</option>
                  <option value="Stitching">Stitching</option>
                  <option value="Accessories">Accessories</option>
                  <option value="General">General</option>
                </select>
                {errors.vendorType && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.vendorType}
                  </p>
                )}
              </div>

              {/* Contact Number */}
              <div>
                <label
                  htmlFor="contactNumber"
                  className="block text-sm font-medium text-gray-700"
                >
                  Contact Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="contactNumber"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  placeholder="e.g., +91 98765 43210"
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.contactNumber ? "border-red-500" : "border-gray-300"
                  } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.contactNumber && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.contactNumber}
                  </p>
                )}
              </div>

              {/* Status */}
              <div>
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-700"
                >
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              {/* Email Address */}
              <div className="md:col-span-1">
                <label
                  htmlFor="emailAddress"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email Address
                </label>
                <input
                  type="email"
                  id="emailAddress"
                  name="emailAddress"
                  value={formData.emailAddress}
                  onChange={handleChange}
                  placeholder="vendor@example.com"
                  className={`mt-1 block w-full px-3 py-2 bg-white border ${
                    errors.emailAddress ? "border-red-500" : "border-gray-300"
                  } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                />
                {errors.emailAddress && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.emailAddress}
                  </p>
                )}
              </div>

              {/* GST Number */}
              <div className="md:col-span-1">
                <label
                  htmlFor="gstNumber"
                  className="block text-sm font-medium text-gray-700"
                >
                  GST Number
                </label>
                <input
                  type="text"
                  id="gstNumber"
                  name="gstNumber"
                  value={formData.gstNumber}
                  onChange={handleChange}
                  placeholder="GST registration number"
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <label
                  htmlFor="address"
                  className="block text-sm font-medium text-gray-700"
                >
                  Address
                </label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Complete address with city, state, pincode"
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              {/* Additional Notes */}
              <div className="md:col-span-2">
                <label
                  htmlFor="additionalNotes"
                  className="block text-sm font-medium text-gray-700"
                >
                  Additional Notes
                </label>
                <textarea
                  id="additionalNotes"
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Any additional information about the vendor"
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateVendor;
